import { supabase } from '../utils/supabase';

/**
 * Payment Ledger Service
 * 
 * Handles payment recording, allocation to sessions, credit management, and undo operations.
 * 
 * Key Features:
 * - Automatic payment allocation to oldest unpaid sessions first
 * - Per-club credit balance management
 * - Full audit trail via payment_ledger and payment_allocations tables
 * - Soft delete for undo capability
 * - Multi-currency support
 * - Manual session toggle support with ledger tracking
 */

/**
 * Get paid status for multiple session_players in a single query (batch operation)
 * 
 * @param {Array<{id: string, sessionPrice: number}>} sessionPlayers - Array of session_player objects with id and sessionPrice
 * @returns {Promise<Object>} Map of sessionPlayerId -> payment status
 */
export async function getBatchSessionPlayerPaidStatus(sessionPlayers) {
  try {
    if (!sessionPlayers || sessionPlayers.length === 0) {
      return { success: true, statusMap: {} };
    }

    const sessionPlayerIds = sessionPlayers.map(sp => sp.id);

    // Get all payment allocations for these session_players in ONE query (include payer and participant for "Paid by X")
    const { data: allocations, error } = await supabase
      .from('payment_allocations')
      .select(`
        session_player_id,
        amount_applied,
        created_at,
        session_player:session_player_id(player_id),
        payment_ledger:payment_id(
          id,
          payment_source,
          payment_method,
          created_at,
          is_deleted,
          payer_id,
          payer:payer_id(id, first_name, last_name)
        )
      `)
      .in('session_player_id', sessionPlayerIds);

    if (error) {
      throw new Error(`Failed to fetch payment allocations: ${error.message}`);
    }

    // Build a map of sessionPlayerId -> payment info
    const statusMap = {};
    
    // Initialize all session_players as unpaid
    sessionPlayers.forEach(sp => {
      statusMap[sp.id] = {
        success: true,
        isPaid: false,
        amountPaid: 0,
        paidViaLedger: false,
        paymentSource: null,
        paymentDate: null,
        paymentMethod: null,
        payerId: null,
        payerFirstName: null,
        payerLastName: null,
        participantPlayerId: sp.playerId ?? null
      };
    });

    // Group allocations by session_player_id
    const allocationsBySessionPlayer = {};
    (allocations || []).forEach(alloc => {
      if (!allocationsBySessionPlayer[alloc.session_player_id]) {
        allocationsBySessionPlayer[alloc.session_player_id] = [];
      }
      allocationsBySessionPlayer[alloc.session_player_id].push(alloc);
    });

    // Compute status for each session_player
    Object.entries(allocationsBySessionPlayer).forEach(([sessionPlayerId, playerAllocations]) => {
      const activeAllocations = playerAllocations.filter(
        a => a.payment_ledger && !a.payment_ledger.is_deleted
      );

      if (activeAllocations.length === 0) {
        return; // Keep default unpaid status
      }

      const totalPaid = activeAllocations.reduce(
        (sum, a) => sum + parseFloat(a.amount_applied),
        0
      );

      const sessionPlayer = sessionPlayers.find(sp => sp.id === sessionPlayerId);
      const sessionPrice = sessionPlayer?.sessionPrice || 0;
      const isPaid = totalPaid >= sessionPrice;

      const firstAllocation = activeAllocations[0];
      const payer = firstAllocation.payment_ledger?.payer;
      const participantPlayerId = firstAllocation.session_player?.player_id ?? sessionPlayer?.playerId;

      const paymentId = firstAllocation.payment_id ?? firstAllocation.payment_ledger?.id ?? null;
      statusMap[sessionPlayerId] = {
        success: true,
        isPaid,
        amountPaid: totalPaid,
        paidViaLedger: true,
        paymentSource: firstAllocation.payment_ledger?.payment_source,
        paymentDate: firstAllocation.created_at,
        paymentMethod: firstAllocation.payment_ledger?.payment_method,
        payerId: firstAllocation.payment_ledger?.payer_id ?? null,
        payerFirstName: payer?.first_name ?? null,
        payerLastName: payer?.last_name ?? null,
        participantPlayerId: participantPlayerId ?? sessionPlayer?.playerId ?? null,
        paymentId
      };
    });

    return {
      success: true,
      statusMap
    };

  } catch (error) {
    console.error('Error in getBatchSessionPlayerPaidStatus:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred',
      statusMap: {}
    };
  }
}

/**
 * Get paid status for a session_player based on payment allocations
 * 
 * @param {string} sessionPlayerId - UUID of the session_player record
 * @param {number} sessionPrice - Price of the session
 * @returns {Promise<Object>} Payment status details
 */
export async function getSessionPlayerPaidStatus(sessionPlayerId, sessionPrice) {
  // Use batch function for single query
  const result = await getBatchSessionPlayerPaidStatus([{ id: sessionPlayerId, sessionPrice }]);
  
  if (!result.success) {
    return {
      success: false,
      isPaid: false,
      amountPaid: 0,
      error: result.error
    };
  }
  
  return result.statusMap[sessionPlayerId] || {
    success: true,
    isPaid: false,
    amountPaid: 0,
    paidViaLedger: false
  };
}

/**
 * Toggle payment status for a single session_player using ledger system
 * 
 * @param {string} sessionPlayerId - UUID of the session_player record
 * @param {Object} session - Session object with id, price_per_player, currency_code
 * @param {string} createdBy - UUID of the user performing the action
 * @returns {Promise<Object>} Result of the toggle operation
 */
export async function toggleSessionPayment(sessionPlayerId, session, createdBy) {
  try {
    // Validate inputs
    if (!sessionPlayerId || !session || !createdBy) {
      throw new Error('Missing required parameters');
    }

    if (!session.price_per_player || session.price_per_player <= 0) {
      throw new Error('Session must have a valid price to toggle payment');
    }

    // Get current payment status
    const statusResult = await getSessionPlayerPaidStatus(
      sessionPlayerId, 
      session.price_per_player
    );

    if (!statusResult.success) {
      throw new Error(statusResult.error);
    }

    if (!statusResult.isPaid) {
      // MARK AS PAID: Create ledger entry with manual_session_toggle source
      
      // Get session_player details for player_id and club_id
      const { data: sessionPlayer, error: spError } = await supabase
        .from('session_players')
        .select('player_id, club_id')
        .eq('id', sessionPlayerId)
        .single();

      if (spError || !sessionPlayer) {
        throw new Error(`Failed to fetch session player: ${spError?.message || 'Not found'}`);
      }

      // Create payment ledger entry (payer = participant when using manual toggle)
      const { data: ledgerEntry, error: ledgerError } = await supabase
        .from('payment_ledger')
        .insert({
          payer_id: sessionPlayer.player_id,
          club_id: sessionPlayer.club_id,
          amount: session.price_per_player,
          currency_code: session.currency_code || 'GBP',
          payment_method: 'other',
          payment_source: 'manual_session_toggle',
          note: `Manual payment toggle for session: ${session.name || 'Unknown'} on ${session.session_date || 'Unknown date'}`,
          created_by: createdBy,
          is_deleted: false
        })
        .select()
        .single();

      if (ledgerError) {
        throw new Error(`Failed to create payment ledger entry: ${ledgerError.message}`);
      }

      // Create payment allocation
      const { error: allocationError } = await supabase
        .from('payment_allocations')
        .insert({
          payment_id: ledgerEntry.id,
          session_player_id: sessionPlayerId,
          amount_applied: session.price_per_player
        });

      if (allocationError) {
        throw new Error(`Failed to create payment allocation: ${allocationError.message}`);
      }

      return {
        success: true,
        action: 'marked_paid',
        paymentId: ledgerEntry.id,
        amount: session.price_per_player
      };

    } else {
      // MARK AS UNPAID: Check if payment source is manual_session_toggle
      
      if (statusResult.paymentSource !== 'manual_session_toggle') {
        throw new Error('This payment was recorded via the ledger system. Please manage it from the Payment Tracking page.');
      }

      // Get the allocation and payment to undo
      const { data: allocations, error: allocError } = await supabase
        .from('payment_allocations')
        .select(`
          id,
          payment_id,
          payment_ledger:payment_id(
            id,
            payment_source,
            is_deleted,
            payer_id,
            club_id,
            amount
          )
        `)
        .eq('session_player_id', sessionPlayerId);

      if (allocError) {
        throw new Error(`Failed to fetch payment allocation: ${allocError.message}`);
      }

      // Find the active manual toggle payment
      const manualAllocation = allocations?.find(
        a => a.payment_ledger?.payment_source === 'manual_session_toggle' 
          && !a.payment_ledger.is_deleted
      );

      if (!manualAllocation) {
        throw new Error('No manual toggle payment found for this session');
      }

      // Soft delete the payment ledger entry
      const { error: deleteError } = await supabase
        .from('payment_ledger')
        .update({
          is_deleted: true,
          deleted_at: new Date().toISOString(),
          deleted_by: createdBy
        })
        .eq('id', manualAllocation.payment_id);

      if (deleteError) {
        throw new Error(`Failed to undo payment: ${deleteError.message}`);
      }

      // Note: payment_allocations will remain for audit trail
      // The soft-delete on payment_ledger is sufficient

      return {
        success: true,
        action: 'marked_unpaid',
        paymentId: manualAllocation.payment_id,
        amount: session.price_per_player
      };
    }

  } catch (error) {
    console.error('Error in toggleSessionPayment:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    };
  }
}

/**
 * Bulk toggle payment status for multiple session_players
 * Used by "Mark all as paid/unpaid" buttons
 * 
 * @param {Array<string>} sessionPlayerIds - Array of session_player IDs
 * @param {Object} session - Session object with id, price_per_player, currency_code
 * @param {string} createdBy - UUID of the user performing the action
 * @param {string} targetStatus - 'paid' or 'unpaid'
 * @returns {Promise<Object>} Result of the bulk operation
 */
export async function bulkToggleSessionPayments(sessionPlayerIds, session, createdBy, targetStatus) {
  try {
    // Validate inputs
    if (!sessionPlayerIds || sessionPlayerIds.length === 0) {
      throw new Error('No session players provided');
    }

    if (!session || !createdBy) {
      throw new Error('Missing required parameters');
    }

    if (!session.price_per_player || session.price_per_player <= 0) {
      throw new Error('Session must have a valid price to toggle payments');
    }

    if (!['paid', 'unpaid'].includes(targetStatus)) {
      throw new Error('Target status must be either "paid" or "unpaid"');
    }

    const results = {
      success: true,
      processed: 0,
      succeeded: 0,
      failed: 0,
      errors: []
    };

    // Get current status for all session_players in ONE batch query
    const sessionPlayersWithPrice = sessionPlayerIds.map(id => ({
      id,
      sessionPrice: session.price_per_player
    }));

    const statusResult = await getBatchSessionPlayerPaidStatus(sessionPlayersWithPrice);
    const statusMap = statusResult.statusMap || {};

    // Process each session_player
    for (const sessionPlayerId of sessionPlayerIds) {
      results.processed++;
      
      try {
        // Get status from batch result
        const playerStatus = statusMap[sessionPlayerId];
        
        if (!playerStatus) {
          results.failed++;
          results.errors.push({
            sessionPlayerId,
            error: 'Failed to get payment status'
          });
          continue;
        }

        // Skip if already in target state
        if (targetStatus === 'paid' && playerStatus.isPaid) {
          results.succeeded++;
          continue;
        }
        if (targetStatus === 'unpaid' && !playerStatus.isPaid) {
          results.succeeded++;
          continue;
        }

        // For unpaid target, check if it's a manual toggle (can be undone)
        if (targetStatus === 'unpaid' && playerStatus.paymentSource !== 'manual_session_toggle') {
          results.failed++;
          results.errors.push({
            sessionPlayerId,
            error: 'Payment managed by ledger - cannot bulk undo'
          });
          continue;
        }

        // Toggle the payment
        const toggleResult = await toggleSessionPayment(
          sessionPlayerId,
          session,
          createdBy
        );

        if (toggleResult.success) {
          results.succeeded++;
        } else {
          results.failed++;
          results.errors.push({
            sessionPlayerId,
            error: toggleResult.error
          });
        }

      } catch (err) {
        results.failed++;
        results.errors.push({
          sessionPlayerId,
          error: err.message
        });
      }
    }

    // If all failed, mark overall as failed
    if (results.succeeded === 0 && results.failed > 0) {
      results.success = false;
    }

    return results;

  } catch (error) {
    console.error('Error in bulkToggleSessionPayments:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    };
  }
}

/**
 * Apply a payment from a player and automatically allocate to unpaid sessions
 * 
 * @param {string} payerId - UUID of the player making the payment (payer; credit belongs to them)
 * @param {string} clubId - UUID of the club
 * @param {number} paymentAmount - Amount paid (positive number)
 * @param {string} createdBy - UUID of the host/admin recording the payment
 * @param {Object} options - Additional options
 * @param {string[]} [options.payForPlayerIds] - Optional: also pay for these participants (e.g. guests). Unpaid sessions for payer + these IDs, oldest first.
 * @param {string} [options.note] - Optional note about the payment
 * @param {string} [options.paymentMethod='other'] - Payment method: 'cash', 'bank_transfer', 'other'
 * @param {string} [options.currencyCode='GBP'] - Currency code
 * @returns {Promise<Object>} Result with sessions paid, amount allocated, and new credit balance
 */
export async function applyPayment(payerId, clubId, paymentAmount, createdBy, options = {}) {
  const { payForPlayerIds, note, paymentMethod = 'other', currencyCode = 'GBP', prioritizeSessionId } = options;

  try {
    // Validate inputs
    if (!payerId || !clubId || !createdBy) {
      throw new Error('Missing required parameters: payerId, clubId, and createdBy are required');
    }

    if (paymentAmount <= 0) {
      throw new Error('Payment amount must be greater than zero');
    }

    // 1. Get unpaid session_players: payer only, or payer + "also paying for" participants (oldest first)
    const playerIdsToPayFor = payForPlayerIds?.length
      ? [payerId, ...payForPlayerIds.filter(id => id !== payerId)]
      : [payerId];

    const { data: sessionPlayers, error: sessionsError } = await supabase
      .from('session_players')
      .select(`
        id,
        session_id,
        player_id,
        player:player_id(first_name, last_name),
        sessions!inner(
          id,
          session_date,
          price_per_player,
          currency_code,
          name
        )
      `)
      .in('player_id', playerIdsToPayFor)
      .eq('club_id', clubId)
      .not('sessions.price_per_player', 'is', null)
      .eq('sessions.currency_code', currencyCode)
      .order('sessions(session_date)', { ascending: true });

    if (sessionsError) {
      console.error('Error fetching sessions:', sessionsError);
      throw new Error(`Failed to fetch sessions: ${sessionsError.message}`);
    }

    // Filter to only unpaid sessions by checking allocations in batch
    const unpaidSessions = [];
    if (sessionPlayers && sessionPlayers.length > 0) {
      const sessionPlayersWithPrice = sessionPlayers.map(sp => ({
        id: sp.id,
        sessionPrice: sp.sessions.price_per_player,
        sessionData: sp
      }));

      const statusResult = await getBatchSessionPlayerPaidStatus(sessionPlayersWithPrice);
      const statusMap = statusResult.statusMap || {};

      sessionPlayers.forEach(sp => {
        const status = statusMap[sp.id];
        if (status && !status.isPaid) {
          unpaidSessions.push(sp);
        }
      });

      // When recording from a session context: put this session's participations first, then oldest first
      if (prioritizeSessionId) {
        unpaidSessions.sort((a, b) => {
          const aCurrent = a.session_id === prioritizeSessionId;
          const bCurrent = b.session_id === prioritizeSessionId;
          if (aCurrent && !bCurrent) return -1;
          if (!aCurrent && bCurrent) return 1;
          if (aCurrent && bCurrent) return 0;
          return new Date(a.sessions.session_date) - new Date(b.sessions.session_date);
        });
      }
    }

    // 2. Get current credit balance from players_club (payer only)
    const { data: membership, error: membershipError } = await supabase
      .from('players_club')
      .select('credit_balance')
      .eq('player_id', payerId)
      .eq('club_id', clubId)
      .single();

    if (membershipError && membershipError.code !== 'PGRST116') {
      console.error('Error fetching membership:', membershipError);
      throw new Error(`Failed to fetch player membership: ${membershipError.message}`);
    }

    const currentCredit = membership?.credit_balance || 0;
    let remaining = paymentAmount + currentCredit;

    // 3. Calculate allocations to sessions (oldest first)
    const allocations = [];
    const sessionsPaid = [];
    
    if (unpaidSessions && unpaidSessions.length > 0) {
      for (const sp of unpaidSessions) {
        const sessionPrice = parseFloat(sp.sessions.price_per_player);
        if (remaining >= sessionPrice) {
          const participantName = sp.player
            ? `${sp.player.first_name} ${sp.player.last_name}`.trim()
            : '';
          allocations.push({
            session_player_id: sp.id,
            amount_applied: sessionPrice,
            session_info: {
              id: sp.sessions.id,
              name: sp.sessions.name,
              session_date: sp.sessions.session_date,
              price: sessionPrice,
              participantName
            }
          });
          sessionsPaid.push(sp.id);
          remaining -= sessionPrice;
        } else {
          // Not enough remaining to pay this session, stop allocating
          break;
        }
      }
    }

    // 4. Insert payment ledger entry (payer_id = who made the payment)
    const { data: ledgerEntry, error: ledgerError } = await supabase
      .from('payment_ledger')
      .insert({
        payer_id: payerId,
        club_id: clubId,
        amount: paymentAmount,
        currency_code: currencyCode,
        payment_method: paymentMethod,
        payment_source: 'lump_payment',
        note,
        created_by: createdBy,
        is_deleted: false
      })
      .select()
      .single();

    if (ledgerError) {
      console.error('Error creating payment ledger entry:', ledgerError);
      throw new Error(`Failed to create payment record: ${ledgerError.message}`);
    }

    // 5. Insert payment allocations (if any sessions were paid)
    if (allocations.length > 0) {
      const { error: allocationsError } = await supabase
        .from('payment_allocations')
        .insert(allocations.map(a => ({
          payment_id: ledgerEntry.id,
          session_player_id: a.session_player_id,
          amount_applied: a.amount_applied
        })));

      if (allocationsError) {
        console.error('Error creating payment allocations:', allocationsError);
        throw new Error(`Failed to create payment allocations: ${allocationsError.message}`);
      }
    }

    // 6. Update credit balance in players_club (payer only)
    const { error: creditError } = await supabase
      .from('players_club')
      .update({ credit_balance: remaining })
      .eq('player_id', payerId)
      .eq('club_id', clubId);

    if (creditError) {
      console.error('Error updating credit balance:', creditError);
      throw new Error(`Failed to update credit balance: ${creditError.message}`);
    }

    // 8. Return success result
    return {
      success: true,
      paymentId: ledgerEntry.id,
      sessionsPaid: allocations.map(a => a.session_info),
      totalAllocated: paymentAmount + currentCredit - remaining,
      previousCredit: currentCredit,
      newCreditBalance: remaining,
      currencyCode
    };

  } catch (error) {
    console.error('Error in applyPayment:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    };
  }
}

/**
 * Undo a payment transaction (soft delete)
 * 
 * @param {string} paymentId - UUID of the payment to undo
 * @param {string} deletedBy - UUID of the host/admin undoing the payment
 * @returns {Promise<Object>} Result of the undo operation
 */
export async function undoPayment(paymentId, deletedBy) {
  try {
    // Validate inputs
    if (!paymentId || !deletedBy) {
      throw new Error('Missing required parameters: paymentId and deletedBy are required');
    }

    // 1. Get payment ledger entry
    const { data: payment, error: paymentError } = await supabase
      .from('payment_ledger')
      .select('*')
      .eq('id', paymentId)
      .eq('is_deleted', false)
      .single();

    if (paymentError) {
      console.error('Error fetching payment:', paymentError);
      throw new Error(`Payment not found or already deleted: ${paymentError.message}`);
    }

    // 2. Get payment allocations
    const { data: allocations, error: allocationsError } = await supabase
      .from('payment_allocations')
      .select('session_player_id, amount_applied')
      .eq('payment_id', paymentId);

    if (allocationsError) {
      console.error('Error fetching allocations:', allocationsError);
      throw new Error(`Failed to fetch payment allocations: ${allocationsError.message}`);
    }

    // 3. Recalculate credit balance
    // Current credit - payment amount + allocated amounts = new credit
    const totalAllocated = allocations?.reduce((sum, a) => sum + parseFloat(a.amount_applied), 0) || 0;
    
    // Get current credit (payer)
    const { data: membership, error: membershipError } = await supabase
      .from('players_club')
      .select('credit_balance')
      .eq('player_id', payment.payer_id)
      .eq('club_id', payment.club_id)
      .single();

    if (membershipError) {
      console.error('Error fetching membership:', membershipError);
      throw new Error(`Failed to fetch player membership: ${membershipError.message}`);
    }

    const currentCredit = membership?.credit_balance || 0;
    const newCredit = currentCredit - payment.amount + totalAllocated;

    if (newCredit < 0) {
      throw new Error('Cannot undo payment: would result in negative credit balance');
    }

    // 4. Update credit balance (payer)
    const { error: creditError } = await supabase
      .from('players_club')
      .update({ credit_balance: newCredit })
      .eq('player_id', payment.payer_id)
      .eq('club_id', payment.club_id);

    if (creditError) {
      console.error('Error updating credit balance:', creditError);
      throw new Error(`Failed to update credit balance: ${creditError.message}`);
    }

    // 5. Soft delete payment ledger entry
    const { error: deleteError } = await supabase
      .from('payment_ledger')
      .update({
        is_deleted: true,
        deleted_at: new Date().toISOString(),
        deleted_by: deletedBy
      })
      .eq('id', paymentId);

    if (deleteError) {
      console.error('Error soft deleting payment:', deleteError);
      throw new Error(`Failed to delete payment: ${deleteError.message}`);
    }

    // 7. Return success result
    return {
      success: true,
      paymentId,
      sessionsRestored: allocations?.length || 0,
      amountRefunded: payment.amount,
      previousCredit: currentCredit,
      newCreditBalance: newCredit
    };

  } catch (error) {
    console.error('Error in undoPayment:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    };
  }
}

/**
 * Get player's balance summary for a club
 * 
 * @param {string} playerId - UUID of the player
 * @param {string} clubId - UUID of the club
 * @param {string} [currencyCode='GBP'] - Currency code to filter sessions
 * @returns {Promise<Object>} Balance summary
 */
export async function getPlayerBalance(playerId, clubId, currencyCode = 'GBP') {
  try {
    // Get credit balance
    const { data: membership, error: membershipError } = await supabase
      .from('players_club')
      .select('credit_balance')
      .eq('player_id', playerId)
      .eq('club_id', clubId)
      .single();

    if (membershipError && membershipError.code !== 'PGRST116') {
      throw new Error(`Failed to fetch credit balance: ${membershipError.message}`);
    }

    const credit = membership?.credit_balance || 0;

    // Get all sessions for this player in this club
    const { data: sessionPlayers, error: sessionsError } = await supabase
      .from('session_players')
      .select(`
        id,
        sessions!inner(
          price_per_player,
          currency_code,
          session_date,
          name
        )
      `)
      .eq('player_id', playerId)
      .eq('club_id', clubId)
      .not('sessions.price_per_player', 'is', null)
      .eq('sessions.currency_code', currencyCode);

    if (sessionsError) {
      throw new Error(`Failed to fetch sessions: ${sessionsError.message}`);
    }

    // Filter to only unpaid sessions by checking allocations in batch
    const unpaidSessions = [];
    if (sessionPlayers && sessionPlayers.length > 0) {
      const sessionPlayersWithPrice = sessionPlayers.map(sp => ({
        id: sp.id,
        sessionPrice: sp.sessions.price_per_player,
        sessionData: sp
      }));

      const statusResult = await getBatchSessionPlayerPaidStatus(sessionPlayersWithPrice);
      const statusMap = statusResult.statusMap || {};

      sessionPlayers.forEach(sp => {
        const status = statusMap[sp.id];
        if (status && !status.isPaid) {
          unpaidSessions.push(sp);
        }
      });
    }

    const unpaidSessionsCount = unpaidSessions.length;
    const unpaidAmount = unpaidSessions?.reduce((sum, sp) => sum + parseFloat(sp.sessions.price_per_player), 0) || 0;

    return {
      success: true,
      credit,
      unpaidSessions: unpaidSessionsCount,
      unpaidAmount,
      netBalance: credit - unpaidAmount,
      currencyCode
    };

  } catch (error) {
    console.error('Error in getPlayerBalance:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    };
  }
}

/**
 * Get player's unpaid sessions (for preview before recording payment)
 * 
 * @param {string} playerId - UUID of the player
 * @param {string} clubId - UUID of the club
 * @param {string} [currencyCode='GBP'] - Currency code to filter sessions
 * @returns {Promise<Object>} List of unpaid sessions
 */
export async function getPlayerUnpaidSessions(playerId, clubId, currencyCode = 'GBP') {
  try {
    const { data: sessionPlayers, error } = await supabase
      .from('session_players')
      .select(`
        id,
        session_id,
        sessions!inner(
          id,
          session_date,
          price_per_player,
          currency_code,
          name
        )
      `)
      .eq('player_id', playerId)
      .eq('club_id', clubId)
      .not('sessions.price_per_player', 'is', null)
      .eq('sessions.currency_code', currencyCode)
      .order('sessions(session_date)', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch sessions: ${error.message}`);
    }

    // Filter to only unpaid sessions by checking allocations in batch
    const unpaidSessions = [];
    if (sessionPlayers && sessionPlayers.length > 0) {
      const sessionPlayersWithPrice = sessionPlayers.map(sp => ({
        id: sp.id,
        sessionPrice: sp.sessions.price_per_player,
        sessionData: sp
      }));

      const statusResult = await getBatchSessionPlayerPaidStatus(sessionPlayersWithPrice);
      const statusMap = statusResult.statusMap || {};

      sessionPlayers.forEach(sp => {
        const status = statusMap[sp.id];
        if (status && !status.isPaid) {
          unpaidSessions.push(sp);
        }
      });
    }

    const sessions = unpaidSessions.map(sp => ({
      sessionPlayerId: sp.id,
      sessionId: sp.sessions.id,
      name: sp.sessions.name,
      date: sp.sessions.session_date,
      price: parseFloat(sp.sessions.price_per_player),
      currency: sp.sessions.currency_code
    }));

    return {
      success: true,
      sessions,
      totalAmount: sessions.reduce((sum, s) => sum + s.price, 0),
      currencyCode
    };

  } catch (error) {
    console.error('Error in getPlayerUnpaidSessions:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    };
  }
}

/**
 * Preview what a payment would do (without actually applying it)
 * 
 * @param {string} payerId - UUID of the payer
 * @param {string} clubId - UUID of the club
 * @param {number} paymentAmount - Amount to preview
 * @param {string} [currencyCode='GBP'] - Currency code
 * @param {Object} [options] - Options
 * @param {string[]} [options.payForPlayerIds] - Optional: also pay for these participants
 * @returns {Promise<Object>} Preview of sessions that would be paid and remaining credit
 */
export async function previewPayment(payerId, clubId, paymentAmount, currencyCode = 'GBP', options = {}) {
  const { payForPlayerIds, prioritizeSessionId } = options;

  try {
    // Get current credit (payer)
    const { data: membership } = await supabase
      .from('players_club')
      .select('credit_balance')
      .eq('player_id', payerId)
      .eq('club_id', clubId)
      .single();

    const currentCredit = membership?.credit_balance || 0;

    // Build unpaid list: payer only, or payer + payForPlayerIds (same logic as applyPayment)
    const playerIdsToPayFor = payForPlayerIds?.length
      ? [payerId, ...payForPlayerIds.filter(id => id !== payerId)]
      : [payerId];

    const { data: sessionPlayers, error: sessionsError } = await supabase
      .from('session_players')
      .select(`
        id,
        session_id,
        player_id,
        player:player_id(first_name, last_name),
        sessions!inner(
          id,
          session_date,
          price_per_player,
          currency_code,
          name
        )
      `)
      .in('player_id', playerIdsToPayFor)
      .eq('club_id', clubId)
      .not('sessions.price_per_player', 'is', null)
      .eq('sessions.currency_code', currencyCode)
      .order('sessions(session_date)', { ascending: true });

    if (sessionsError) {
      throw new Error(`Failed to fetch sessions: ${sessionsError.message}`);
    }

    let unpaidSessions = [];
    if (sessionPlayers?.length > 0) {
      const sessionPlayersWithPrice = sessionPlayers.map(sp => ({
        id: sp.id,
        sessionPrice: sp.sessions.price_per_player,
        sessionData: sp
      }));
      const statusResult = await getBatchSessionPlayerPaidStatus(sessionPlayersWithPrice);
      const statusMap = statusResult.statusMap || {};
      sessionPlayers.forEach(sp => {
        const status = statusMap[sp.id];
        if (status && !status.isPaid) {
          unpaidSessions.push(sp);
        }
      });

      // When recording from a session context: put this session's participations first, then oldest first
      if (prioritizeSessionId) {
        unpaidSessions.sort((a, b) => {
          const aCurrent = a.session_id === prioritizeSessionId;
          const bCurrent = b.session_id === prioritizeSessionId;
          if (aCurrent && !bCurrent) return -1;
          if (!aCurrent && bCurrent) return 1;
          if (aCurrent && bCurrent) return 0;
          return new Date(a.sessions.session_date) - new Date(b.sessions.session_date);
        });
      }
    }

    let remaining = paymentAmount + currentCredit;
    const sessionsToBePaid = [];
    for (const sp of unpaidSessions) {
      const sessionPrice = parseFloat(sp.sessions.price_per_player);
      if (remaining >= sessionPrice) {
        const participantName = sp.player
          ? `${sp.player.first_name} ${sp.player.last_name}`.trim()
          : '';
        sessionsToBePaid.push({
          sessionPlayerId: sp.id,
          sessionId: sp.sessions.id,
          name: sp.sessions.name,
          date: sp.sessions.session_date,
          price: sessionPrice,
          currency: sp.sessions.currency_code,
          participantName,
          participantPlayerId: sp.player_id
        });
        remaining -= sessionPrice;
      } else {
        break;
      }
    }

    return {
      success: true,
      sessionsToBePaid,
      currentCredit,
      paymentAmount,
      totalAvailable: paymentAmount + currentCredit,
      amountToBeAllocated: paymentAmount + currentCredit - remaining,
      remainingCredit: remaining,
      currencyCode
    };

  } catch (error) {
    console.error('Error in previewPayment:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    };
  }
}

/**
 * Get payment ledger history for a club
 * 
 * @param {string} clubId - UUID of the club
 * @param {Object} filters - Optional filters
 * @param {string} [filters.playerId] - Filter by player
 * @param {string} [filters.startDate] - Filter by start date (ISO string)
 * @param {string} [filters.endDate] - Filter by end date (ISO string)
 * @param {string} [filters.paymentMethod] - Filter by payment method
 * @param {boolean} [filters.includeDeleted=false] - Include deleted payments
 * @param {number} [filters.limit=100] - Maximum number of results
 * @returns {Promise<Object>} Payment ledger entries
 */
export async function getPaymentLedger(clubId, filters = {}) {
  try {
    const {
      playerId,
      payerId,
      startDate,
      endDate,
      paymentMethod,
      includeDeleted = false,
      limit = 100
    } = filters;

    const filterByPayerId = payerId ?? playerId;

    let query = supabase
      .from('payment_ledger')
      .select(`
        *,
        payer:payer_id(id, first_name, last_name),
        created_by_player:created_by(id, first_name, last_name),
        deleted_by_player:deleted_by(id, first_name, last_name)
      `)
      .eq('club_id', clubId);

    if (!includeDeleted) {
      query = query.eq('is_deleted', false);
    }

    if (filterByPayerId) {
      query = query.eq('payer_id', filterByPayerId);
    }

    if (startDate) {
      query = query.gte('created_at', startDate);
    }

    if (endDate) {
      query = query.lte('created_at', endDate);
    }

    if (paymentMethod) {
      query = query.eq('payment_method', paymentMethod);
    }

    query = query.order('created_at', { ascending: false }).limit(limit);

    const { data: payments, error } = await query;

    if (error) {
      throw new Error(`Failed to fetch payment ledger: ${error.message}`);
    }

    return {
      success: true,
      payments: payments || []
    };

  } catch (error) {
    console.error('Error in getPaymentLedger:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    };
  }
}

/**
 * Get allocations for a specific payment
 * 
 * @param {string} paymentId - UUID of the payment
 * @returns {Promise<Object>} Payment allocations with session details
 */
export async function getPaymentAllocations(paymentId) {
  try {
    const { data: allocations, error } = await supabase
      .from('payment_allocations')
      .select(`
        *,
        session_player:session_player_id(
          id,
          session_id,
          sessions(id, name, session_date)
        )
      `)
      .eq('payment_id', paymentId)
      .order('created_at', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch payment allocations: ${error.message}`);
    }

    return {
      success: true,
      allocations: allocations || []
    };

  } catch (error) {
    console.error('Error in getPaymentAllocations:', error);
    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    };
  }
}
